// frontend/src/pages/admin/Settings.jsx
import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import LoginSettingsTab from '../../components/admin/LoginSettingsTab';
import NotificationTab from '../../components/admin/NotificationTab';
import { systemBankAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Settings = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('bank');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    iban_number: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Эрхүүд
  const canEdit = hasPermission('system.settings.edit');
  const canViewLogin = hasPermission('system.login_settings.view');
  const canEditLogin = hasPermission('system.login_settings.edit');

  // Fetch bank accounts
  const fetchBankAccounts = async () => {
    setLoading(true);
    try {
      const response = await systemBankAPI.getAll();
      if (response.data.success) {
        setBankAccounts(response.data.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'bank') {
      fetchBankAccounts();
    }
  }, [activeTab]);

  // Open modal for new account
  const handleAddNew = () => {
    if (!canEdit) return;
    setEditingAccount(null);
    setFormData({
      bank_name: '',
      account_number: '',
      account_name: '',
      iban_number: '',
      description: ''
    });
    setError('');
    setShowModal(true);
  };

  // Open modal for edit
  const handleEdit = (account) => {
    if (!canEdit) return;
    setEditingAccount(account);
    setFormData({
      bank_name: account.bank_name,
      account_number: account.account_number,
      account_name: account.account_name,
      iban_number: account.iban_number || '',
      description: account.description || ''
    });
    setError('');
    setShowModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    if (!formData.bank_name || !formData.account_number || !formData.account_name) {
      setError('Банкны нэр, дансны дугаар, данс эзэмшигчийн нэр заавал шаардлагатай');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingAccount) {
        await systemBankAPI.update(editingAccount.id, formData);
      } else {
        await systemBankAPI.create(formData);
      }
      setShowModal(false);
      fetchBankAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (account) => {
    if (!canEdit) return;
    if (!window.confirm(`"${account.bank_name} - ${account.account_number}" дансыг устгах уу?`)) {
      return;
    }

    try {
      await systemBankAPI.delete(account.id);
      fetchBankAccounts();
    } catch (err) {
      alert(err.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  // Toggle status
  const handleToggleStatus = async (account) => {
    if (!canEdit) return;
    try {
      await systemBankAPI.update(account.id, { is_active: !account.is_active });
      fetchBankAccounts();
    } catch (err) {
      alert('Төлөв өөрчлөхөд алдаа гарлаа');
    }
  };

  // Tabs - Login эрхтэй бол харуулах
  const tabs = [
    { id: 'bank', label: 'Дансны тохиргоо', icon: '🏦' },
    // Login тохиргоо харах эрхтэй бол харуулах
    ...(canViewLogin || canEditLogin ? [{ id: 'login', label: 'Login хуудас', icon: '🎨' }] : []),
    { id: 'notification', label: 'Мэдэгдэл', icon: '🔔' },
    { id: 'general', label: 'Ерөнхий', icon: '⚙️', disabled: true }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Тохиргоо</h1>
            <p className="text-gray-600 mt-1">Системийн тохиргоог удирдах</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-4 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : tab.disabled
                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
                {tab.disabled && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Удахгүй</span>}
              </button>
            ))}
          </nav>
        </div>

        {/* Bank Accounts Tab Content */}
        {activeTab === 'bank' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Банкны данснууд</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Компаниуд шимтгэлийн төлбөрөө эдгээр данс руу шилжүүлнэ
                </p>
              </div>
              {/* Засах эрхтэй бол л товч харуулах */}
              {canEdit && (
                <button
                  onClick={handleAddNew}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
                >
                  <span>➕</span>
                  Данс нэмэх
                </button>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500">Уншиж байна...</p>
                </div>
              ) : bankAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-6xl">🏦</span>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Данс бүртгээгүй байна</h3>
                  <p className="mt-2 text-gray-500">Эхлээд данс нэмнэ үү</p>
                  {canEdit && (
                    <button
                      onClick={handleAddNew}
                      className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                    >
                      Данс нэмэх
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Банк</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Дансны дугаар</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Эзэмшигч</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">IBAN</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Төлөв</th>
                        {canEdit && (
                          <th className="text-right py-3 px-4 font-medium text-gray-600">Үйлдэл</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {bankAccounts.map(account => (
                        <tr key={account.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">🏦</span>
                              <span className="font-medium text-gray-900">{account.bank_name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                              {account.account_number}
                            </code>
                          </td>
                          <td className="py-4 px-4 text-gray-700">{account.account_name}</td>
                          <td className="py-4 px-4 text-gray-500 text-sm">
                            {account.iban_number || '-'}
                          </td>
                          <td className="py-4 px-4">
                            {canEdit ? (
                              <button
                                onClick={() => handleToggleStatus(account)}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  account.is_active
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {account.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
                              </button>
                            ) : (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                account.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {account.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
                              </span>
                            )}
                          </td>
                          {canEdit && (
                            <td className="py-4 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEdit(account)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                  title="Засах"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDelete(account)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title="Устгах"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Login Settings Tab Content */}
        {activeTab === 'login' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <LoginSettingsTab />
          </div>
        )}
        {/* Notification Tab Content */}
        {activeTab === 'notification' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <NotificationTab />
          </div>
        )}
      </div>

      {/* Modal - Засах эрхтэй үед л харуулах */}
      {showModal && canEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingAccount ? 'Данс засах' : 'Шинэ данс нэмэх'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Банкны нэр <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Сонгоно уу</option>
                  <option value="Хаан банк">Хаан банк</option>
                  <option value="Голомт банк">Голомт банк</option>
                  <option value="Худалдаа хөгжлийн банк">Худалдаа хөгжлийн банк</option>
                  <option value="Хас банк">Хас банк</option>
                  <option value="Төрийн банк">Төрийн банк</option>
                  <option value="Капитрон банк">Капитрон банк</option>
                  <option value="Богд банк">Богд банк</option>
                  <option value="Үндэсний хөрөнгө оруулалтын банк">Үндэсний хөрөнгө оруулалтын банк</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дансны дугаар <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="1234567890"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Данс эзэмшигчийн нэр <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  placeholder="НАЦАГДОРЖ БЭХБАТ"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IBAN дугаар
                </label>
                <input
                  type="text"
                  value={formData.iban_number}
                  onChange={(e) => setFormData({ ...formData, iban_number: e.target.value })}
                  placeholder="MN58 0015 00 ..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Тайлбар
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Нэмэлт тайлбар..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50"
                >
                  {saving ? 'Хадгалж байна...' : editingAccount ? 'Хадгалах' : 'Нэмэх'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Settings;